'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import { Section, Container } from '@layout';
import { Input, Button, InputAffix, Heading, Card } from '@ui';
import { withOptionalId } from '@lib/withOptionalId';
import MarkupSnippet, { markupCopyLabels } from '../markup-snippet/MarkupSnippet';

/**
 * AsyncInlineValidation
 * Port of info/biotech-layout/js/inline-async-validation.js
 * Uses existing UI primitives and preserves legacy IDs for parity.
 */
type Props = {
  sectionID?: string;
  classes?: string;
  containerId?: string;
  containerClasses?: string;
  cardClassName?: string;
  isSpanish?: boolean;
};

const AsyncInlineValidation = ({
  isSpanish = false,
  sectionID,
  classes = '',
  containerId,
  containerClasses,
  cardClassName,
}: Props) => {
  const t = (en: string, es: string) => (isSpanish ? es : en);

  const formRef = useRef<HTMLFormElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const iconRef = useRef<HTMLSpanElement | null>(null);
  const errRef = useRef<HTMLElement | null>(null);
  const hintRef = useRef<HTMLElement | null>(null);
  const submitRef = useRef<HTMLButtonElement | null>(null);

  const lastValueRef = useRef('');

  const isEmail = useCallback((v: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }, []);

  function checkEmailAvailability(v: string) {
    return new Promise<boolean>((resolve) => {
      setTimeout(
        () => {
          const taken = /taken/i.test(v) || /@exists\.com$/i.test(v);
          resolve(!taken);
        },
        600 + Math.random() * 400
      );
    });
  }

  const resetUI = useCallback(() => {
    if (iconRef.current) iconRef.current.className = 'affix-icon';
    if (errRef.current) {
      errRef.current.hidden = true;
      errRef.current.textContent = '';
    }
    if (hintRef.current) hintRef.current.className = 'hint';
    if (emailRef.current) emailRef.current.removeAttribute('aria-invalid');
    if (submitRef.current) submitRef.current.disabled = true;
  }, []);

  const setLoading = useCallback(() => {
    if (iconRef.current) iconRef.current.className = 'affix-icon is-loading';
    if (submitRef.current) submitRef.current.disabled = true;
  }, []);

  const setResult = useCallback(
    (ok: boolean) => {
      if (iconRef.current) iconRef.current.className = 'affix-icon ' + (ok ? 'ok' : 'err');
      if (ok) {
        if (errRef.current) errRef.current.hidden = true;
        if (emailRef.current) emailRef.current.removeAttribute('aria-invalid');
        if (submitRef.current) submitRef.current.disabled = false;
        if (hintRef.current) hintRef.current.className = 'hint';
        if (hintRef.current)
          hintRef.current.textContent = t('Email is available.', 'El correo está disponible.');
      } else {
        if (errRef.current) {
          errRef.current.hidden = false;
          errRef.current.textContent = t(
            'That email is already taken.',
            'Ese correo ya está en uso.'
          );
        }
        if (emailRef.current) emailRef.current.setAttribute('aria-invalid', 'true');
        if (submitRef.current) submitRef.current.disabled = true;
        if (hintRef.current) hintRef.current.className = 'hint';
        if (hintRef.current)
          hintRef.current.textContent = t('Try another email.', 'Intenta con otro correo.');
      }
    },
    [t]
  );

  // debounce helper
  const debounce = useCallback((fn: (...args: unknown[]) => void, ms = 350) => {
    let t: ReturnType<typeof setTimeout> | undefined;
    return (...args: unknown[]) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        // cast to a compatible function type to avoid spreading unknown[] directly
        (fn as (...a: unknown[]) => void)(...args);
      }, ms);
    };
  }, []);

  useEffect(() => {
    resetUI();
  }, [resetUI]);

  useEffect(() => {
    const email = emailRef.current;
    if (!email) return;

    const onInput = debounce(async () => {
      const v = (email.value || '').trim();
      lastValueRef.current = v;
      resetUI();
      if (!v || !isEmail(v)) {
        if (hintRef.current) {
          hintRef.current.className = v ? 'hint error' : 'hint';
          hintRef.current.textContent = v
            ? t('Enter a valid email.', 'Ingresa un correo válido.')
            : t(
                'Type an email to check availability.',
                'Escribe un correo para verificar la disponibilidad.'
              );
        }
        return;
      }
      setLoading();
      const ok = await checkEmailAvailability(v);
      if (v !== lastValueRef.current) return; // ignore obsolete responses
      setResult(ok);
    }, 350);

    email.addEventListener('input', onInput as EventListener);
    return () => {
      email.removeEventListener('input', onInput as EventListener);
    };
  }, [debounce, isEmail, resetUI, setLoading, setResult]);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const onSubmit = (e: Event) => {
      e.preventDefault();
      // Use window.Toast if present for parity with legacy demo
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const toast = (window as any).Toast;
      if (toast) {
        const emailVal = emailRef.current?.value ?? '';
        toast({
          type: 'success',
          title: t('Account created', 'Cuenta creada'),
          message: t(`Welcome, ${emailVal}!`, `¡Bienvenido, ${emailVal}!`),
        });
      }
      form.reset();
      resetUI();
      if (hintRef.current)
        hintRef.current.textContent = t(
          'Type an email to check availability.',
          'Escribe un correo para verificar la disponibilidad.'
        );
    };
    form.addEventListener('submit', onSubmit);
    return () => form.removeEventListener('submit', onSubmit);
  }, [resetUI, t]);

  return (
    <Section {...withOptionalId(sectionID)} className={classes}>
      <Container containerId={containerId} containerClasses={containerClasses}>
        <Heading
          as='h3'
          title={t('Inline Async Validation', 'Validación Asíncrona en Línea')}
          description={t(
            'Debounced “email already taken?” check with loading state and accessible messages.',
            'Verificación “¿el correo ya está en uso?” con debounce, estado de carga y mensajes accesibles.'
          )}
        />

        <Card className={cardClassName}>
          <form ref={formRef} id='asyncForm' noValidate>
            <div className='grid-2 form-grid'>
              <fieldset>
                <InputAffix
                  ref={emailRef}
                  id='avEmail'
                  placeholder='email@domain.com'
                  autoComplete='email'
                  required
                  label={
                    <>
                      {t('Email', 'Correo')} <b className='req'>*</b>
                    </>
                  }
                  iconId='avIcon'
                  iconRef={iconRef}
                  errorHint=''
                  errorHintId='avErr'
                  errorHintHidden
                  errorHintRef={errRef}
                  hint={t(
                    'Type an email to check availability.',
                    'Escribe un correo para verificar la disponibilidad.'
                  )}
                  hintId='avHint'
                  hintRef={hintRef}
                />
              </fieldset>

              <fieldset>
                <Input
                  className=''
                  id='avUser'
                  placeholder={t('optional', 'opcional')}
                  label={t('Username', 'Nombre de usuario')}
                />
              </fieldset>
            </div>

            <div className='row mt-16' style={{ justifyContent: 'flex-end' }}>
              <Button
                ref={submitRef}
                label={t('Create Account', 'Crear cuenta')}
                id='avSubmit'
                className='btn-primary'
                type='submit'
                disabled
              />
            </div>
          </form>
        </Card>
      <MarkupSnippet
          {...markupCopyLabels(isSpanish)}
          code={`<fieldset class="fieldset">
  <label for="username">Username</label>
  <input id="username" type="text" />
  <small class="hint">Checking availability…</small>
</fieldset>`}
        />
      </Container>
    </Section>
  );
};

export default AsyncInlineValidation;
